import { faChevronDown } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Stack, Typography } from "@mui/material"
import { memo, useCallback, useState } from "react"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"

import { ResourceUsageDetail } from "./ResourceUsageDetail"
import { SegmentedBar } from "./SegmentedBar"
import type { ResourceTenantSegment, ResourceUsageViewModel } from "./tenantUsageViewModel"

const useStyles = makeStyles()((theme) => ({
    root: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    header: {
        padding: "1rem .25rem",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
        },
    },
    name: {
        flexGrow: 1,
        minWidth: "8rem",
        color: theme.palette.tailwind.slate[700],
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    barWrapper: {
        flex: "0 0 45%",
        maxWidth: "28rem",
    },
    chevron: {
        flex: "0 0 auto",
        color: theme.palette.tailwind.slate[400],
        transition: "transform .15s ease",
    },
    chevronExpanded: {
        transform: "rotate(180deg)",
    },
    detailWrapper: {
        padding: "0 .25rem 1rem .25rem",
    },
}))

type ResourceUsageRowProps = Readonly<{
    resource: ResourceUsageViewModel
    isExpanded: boolean
    onToggle: (resourceId: number) => void
}>

function ResourceUsageRowComponent({ resource, isExpanded, onToggle }: ResourceUsageRowProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)

    const getSegmentLabel = useCallback(
        (segment: ResourceTenantSegment) => {
            const name = segment.isOther
                ? l10n.getString("service-center-usage-per-tenant-detail-other-tenants", {
                      count: segment.aggregatedCount,
                  })
                : segment.name
            return `${name} · ${segment.usageRate}%`
        },
        [l10n],
    )

    return (
        <Box className={classes.root}>
            <Stack
                direction="row"
                alignItems="center"
                gap="1.5rem"
                className={classes.header}
                onClick={() => onToggle(resource.resourceId)}
            >
                <Typography variant="body2" className={classes.name} title={resource.displayName}>
                    {resource.displayName}
                </Typography>
                <Box className={classes.barWrapper}>
                    <SegmentedBar
                        segments={resource.segments}
                        hoveredKey={hoveredKey}
                        onHoverSegment={setHoveredKey}
                        getSegmentLabel={getSegmentLabel}
                    />
                </Box>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={cx(classes.chevron, isExpanded && classes.chevronExpanded)}
                />
            </Stack>
            {isExpanded && (
                <Box className={classes.detailWrapper}>
                    <ResourceUsageDetail
                        segments={resource.segments}
                        hoveredKey={hoveredKey}
                        onHoverSegment={setHoveredKey}
                    />
                </Box>
            )}
        </Box>
    )
}

export const ResourceUsageRow = memo(ResourceUsageRowComponent)
